import { Badge } from '@ooh/ui/components/badge';
import { Button } from '@ooh/ui/components/button';
import { Label } from '@ooh/ui/components/label';
import {
  MultiSelectCombobox,
  type MultiSelectComboboxOption,
} from '@ooh/ui/components/multi-select-combobox';
import type { Permission } from '@ooh/contracts';

export type OverrideState = 'inherit' | 'grant' | 'revoke';

export const OVERRIDE_CYCLE: OverrideState[] = ['inherit', 'grant', 'revoke'];
export function nextOverrideState(s: OverrideState): OverrideState {
  return OVERRIDE_CYCLE[(OVERRIDE_CYCLE.indexOf(s) + 1) % OVERRIDE_CYCLE.length]!;
}

export function grantedToOverrideState(granted: boolean | undefined): OverrideState {
  if (granted === undefined) return 'inherit';
  return granted ? 'grant' : 'revoke';
}

export const OVERRIDE_BADGE_CLASS: Record<OverrideState, string> = {
  inherit: 'bg-muted text-muted-foreground',
  grant: 'bg-emerald-100 text-emerald-700',
  revoke: 'bg-red-100 text-red-700',
};

interface OverrideRowProps {
  perm: Permission;
  state: OverrideState;
  onToggle: () => void;
  stateLabel: string;
}

export function OverrideRow({ perm, state, onToggle, stateLabel }: OverrideRowProps) {
  return (
    <div className="flex items-center justify-between py-1.5 px-3 rounded-md hover:bg-muted/40">
      <span className="text-[13px]">
        {perm.name}
        <span className="ml-1.5 font-mono text-[10px] text-muted-foreground/60">{perm.code}</span>
      </span>
      <Badge
        variant="outline"
        className={`text-[11px] cursor-pointer ${OVERRIDE_BADGE_CLASS[state]}`}
        onClick={onToggle}
      >
        {stateLabel}
      </Badge>
    </div>
  );
}

export interface RolePickerFieldProps {
  value: number[];
  onChange: (values: number[]) => void;
  options: MultiSelectComboboxOption[];
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  /** Defaults to 'edit-role' — pass 'create-role' from the create dialog. */
  testId?: string;
}

/** Presentational wrapper so callers only wire an RHF `Controller` around flat props. */
export function RolePickerField({
  value,
  onChange,
  options,
  label,
  placeholder,
  searchPlaceholder,
  emptyText,
  testId = 'edit-role',
}: RolePickerFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <MultiSelectCombobox
        options={options}
        selectedValues={value.map(String)}
        onChange={(vs) => onChange(vs.map(Number))}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyText={emptyText}
        aria-label={label}
        data-testid={testId}
      />
    </div>
  );
}

export interface OverrideEntry {
  code: string;
  resource: string;
  name: string;
  state: OverrideState;
}

interface PermissionOverridesSectionProps {
  sections: [string, Permission[]][];
  overrides: OverrideEntry[];
  overrideStateLabel: Record<OverrideState, string>;
  onToggle: (code: string, current: OverrideEntry[]) => void;
  title: string;
  description: string;
  sectionLabel: (resource: string) => string;
}

export function PermissionOverridesSection({
  sections,
  overrides,
  overrideStateLabel,
  onToggle,
  title,
  description,
  sectionLabel,
}: PermissionOverridesSectionProps) {
  return (
    <div>
      <h3 className="text-[13px] font-semibold mb-2">{title}</h3>
      <p className="text-[12px] text-muted-foreground mb-3">{description}</p>
      <div className="space-y-3">
        {sections.map(([resource, perms]) => (
          <div key={resource}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              {sectionLabel(resource)}
            </p>
            <div className="space-y-1">
              {perms.map((perm) => {
                const found = overrides.find((o) => o.code === perm.code);
                const state: OverrideState = found?.state ?? 'inherit';
                return (
                  <OverrideRow
                    key={perm.code}
                    perm={perm}
                    state={state}
                    onToggle={() => onToggle(perm.code, overrides)}
                    stateLabel={overrideStateLabel[state]}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface DeactivateConfirmDialogProps {
  isPending: boolean;
  title: string;
  description: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeactivateConfirmDialog({
  isPending,
  title,
  description,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: DeactivateConfirmDialogProps) {
  return (
    <>
      <div aria-hidden="true" className="fixed inset-0 z-[60] bg-black/30" onClick={onCancel} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="deactivate-dialog-title"
        aria-describedby="deactivate-dialog-desc"
        className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none"
      >
        <div className="bg-background rounded-xl shadow-xl border border-border w-full max-w-sm mx-6 p-6 space-y-4 pointer-events-auto">
          <div>
            <h3 id="deactivate-dialog-title" className="text-[15px] font-semibold">
              {title}
            </h3>
            <p id="deactivate-dialog-desc" className="text-[13px] text-muted-foreground mt-1">
              {description}
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onConfirm}
              disabled={isPending}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
